import { ReactNode } from 'react';

import {
  BonusIcon,
  ITabOption,
  Label,
  Paragraph,
  TTranslator,
  WalletArrowLeftIcon,
  WalletArrowRightIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { CompositePaymentTooltip, PaymentItemsTooltip } from 'components';
import { useIsFeatureAvailable } from 'features';
import { TLocalesKeys } from 'localization';
import { EBonusType, TBonusItem, TClientClub, TTransfersItem } from 'schemas';
import { EPaymentItemType, EPaymentMethod, IPaymentHistoryItem } from 'types';
import { formatSum, getPaymentMethodLabel, getUserFIO } from 'utils';

import s from './Profile.module.scss';

export const getColumnsPayments = (
  t: TTranslator<TLocalesKeys>,
  currency: { value: string; sign: string }
): Array<Column<IPaymentHistoryItem>> => [
  {
    Header: t('date'),
    accessor: (payment) => dayjs(payment.created_at).valueOf(),
    Cell: ({ value }: { value: number }) =>
      dayjs(value).format('DD.MM.YYYY HH:mm'),
    width: 172,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('roles.operator'),
    accessor: ({ worker }) => (worker ? getUserFIO(worker) : null),
    Cell: ({ value }: { value: string }) => (
      <span className={s.operator}>{value}</span>
    ),
    width: 128,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('category'),
    accessor: 'items',
    Cell: ({ value }: { value: IPaymentHistoryItem['items'] }) =>
      value.length === 1 &&
      (value[0]?.type === 'DEPOSIT' || value[0]?.type === 'BONUS') ? (
        value[0]?.type === 'DEPOSIT' ? (
          t('payments.category.deposit')
        ) : (
          t('payments.category.bonuses')
        )
      ) : (
        <PaymentItemsTooltip items={value} />
      ),
    width: 184,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('payment'),
    accessor: 'paymentMethod',
    Cell: ({ value }) => getPaymentMethodLabel(value, t),
    width: 152,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('total'),
    accessor: 'sum',
    Cell: ({
      value,
      row: {
        original: { paymentMethod, card_sum, cash_sum, bonus, items },
      },
    }) =>
      items.length === 1 && items[0].type === 'BONUS' ? (
        <div className={s.bonuses}>
          <BonusIcon size="s" /> <span>{value}</span>{' '}
        </div>
      ) : paymentMethod === EPaymentMethod.COMPOSITE ? (
        <div className={s.payment_sum}>
          <CompositePaymentTooltip
            cash={cash_sum}
            card={card_sum}
            total={value}
          />
          {Boolean(bonus) && (
            <Label
              iconName={
                items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                  ? 'BonusAddIcon'
                  : 'BonusRemoveIcon'
              }
              size="s"
              color={
                items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                  ? 'green'
                  : 'red'
              }
              text={String(bonus)}
            />
          )}
        </div>
      ) : (
        <div className={s.payment_sum}>
          {formatSum(value, currency.sign, { maximumFractionDigits: 2 })}
          {Boolean(bonus) && (
            <Label
              iconName={
                items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                  ? 'BonusAddIcon'
                  : 'BonusRemoveIcon'
              }
              size="s"
              color={
                items?.some((i) => i.type === EPaymentItemType.DEPOSIT)
                  ? 'green'
                  : 'red'
              }
              text={String(bonus)}
            />
          )}
        </div>
      ),
    width: 185,
    disableSortBy: true,
  },
  {
    accessor: 'is_refunded',
    Cell: ({ value }) =>
      value && (
        <Label
          color="gray-primary"
          text={t('payment.refunded')}
          iconName="CircleCloseIcon"
          rounded
        />
      ),
    width: 138,
    disableSortBy: true,
    disableResizing: true,
  },
];

export const getColumnsBonus = (
  t: TTranslator<TLocalesKeys>
): Array<Column<TBonusItem>> => [
  {
    Header: t('date'),
    accessor: ({ created_at }) => dayjs(created_at).valueOf(),
    Cell: ({ value }: { value: number }) =>
      dayjs(value).format('DD.MM.YYYY HH:mm'),
    width: 242,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('worker'),
    accessor: ({ worker_first_name, worker_last_name }) => {
      if (!worker_first_name && !worker_last_name) return null;
      else if (worker_last_name && worker_first_name)
        return `${worker_last_name} ${worker_first_name[0]}.`;
      else if (worker_last_name || worker_first_name)
        return worker_last_name ?? worker_first_name;
    },
    Cell: ({ value }: { value: string }) => (
      <Paragraph color="secondary">{value}</Paragraph>
    ),
    width: 182,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('type'),
    accessor: ({ add }) =>
      add ? t('warehouse.add.notify') : t('bonuses.outcome'),
    width: 179,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('bonuses.type'),
    accessor: 'type',
    Cell: ({ value }) => getBonusTypeLabel(value, t),
    width: 218,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('sum'),
    accessor: ({ delta, add }) => ({ delta, add }),
    Cell: ({
      value: { delta, add },
    }: {
      value: { delta: number; add: boolean };
    }) => (
      <Label
        color={add ? 'green' : 'red'}
        size="s"
        text={String(Math.ceil(Math.abs(delta)))}
        iconName={add ? 'BonusAddIcon' : 'BonusRemoveIcon'}
      />
    ),
    width: 144,
    disableResizing: true,
    disableSortBy: true,
  },
];

export const getColumnsTransfers = (
  t: TTranslator<TLocalesKeys>,
  currency: { value: string; sign: string }
): Array<Column<TTransfersItem>> => [
  {
    Header: t('payment.date'),
    accessor: ({ created_at }) => dayjs(created_at).valueOf(),
    Cell: ({ value }: { value: number }) =>
      dayjs(value).format('DD.MM.YYYY HH:mm'),
    width: 245,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('warehouse.operation'),
    accessor: 'type',
    Cell: ({ row }) => (
      <div className={cn(s.type, row.original.type === 'DEPOSIT' && s.deposit)}>
        {row.original.type === 'DEPOSIT' ? (
          <>
            <WalletArrowLeftIcon color={'green'} />
            <span>{t('renewal')}</span>
          </>
        ) : (
          <>
            <WalletArrowRightIcon color={'secondary'} />
            <span>{t('warehouse.operation.disposal')}</span>
          </>
        )}
      </div>
    ),
    width: 245,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('club.title'),
    accessor: 'club',
    Cell: ({ value }: { value: TClientClub }) => (
      <div className={s.club}>
        <span className={s.club_name}>{value.name}</span>
        <span className={s.club_id}>ID: {value.id}</span>
      </div>
    ),
    width: 245,
    disableResizing: true,
    disableSortBy: true,
  },

  {
    Header: t('sum'),
    accessor: 'sum',
    Cell: ({ row }) => (
      <div className={s.sum}>
        {row.original.type === 'DEPOSIT' ? (
          <span>
            + {row.original.sum} {currency.sign}
          </span>
        ) : (
          <span>
            - {row.original.sum} {currency.sign}
          </span>
        )}
      </div>
    ),
    width: 200,
    disableResizing: true,
    disableSortBy: true,
  },
];

export const getBonusTypeLabel = (
  bonusType: EBonusType,
  t: TTranslator<TLocalesKeys>
): ReactNode => {
  switch (bonusType) {
    case EBonusType.ACHIEVEMENT:
      return (
        <Label
          text={t('bonuses.achievements')}
          color="green"
          size="s"
          iconName="AchievementsIcon"
        />
      );
    case EBonusType.CASHBACK:
      return (
        <Label
          iconName="CashbackIcon"
          color="blue"
          text={t('cashback')}
          size="s"
        />
      );
    case EBonusType.MANAGER:
      return (
        <Label
          size="s"
          text={t('bonuses.set_manager')}
          color="gray-primary"
          iconName="WalletIcon"
        />
      );
    case EBonusType.PROMOCODE:
      return (
        <Label
          size="s"
          text={t('promoCode')}
          iconName="CouponIcon"
          color="orange"
        />
      );
    case EBonusType.USED:
      return <Label size="s" text={t('bonuses.buy')} color="red" />;
    case EBonusType.REFUND:
      return <Label size="s" text={t('payment.refunded')} color="orange" />;
    default:
      return null;
  }
};

export const getTabs = (
  t: TTranslator<TLocalesKeys>
): Array<ITabOption<'payments' | 'bonus' | 'transfers'>> => {
  const isAvailable = useIsFeatureAvailable('deposit_transfer');

  const tabs: Array<ITabOption<'payments' | 'bonus' | 'transfers'>> = [
    {
      value: 'payments',
      title: t('payments'),
    },
    { value: 'bonus', title: t('nav.loyalty') },
  ];

  if (isAvailable) {
    tabs.push({ value: 'transfers', title: t('transfers') });
  }

  return tabs;
};
